import { useMemo } from 'react';
import { NumberBlockV1 } from '@frontend/api-sms-number-block';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import type { GroupParticipant } from '@frontend/thread-header-context';

type UseGroupBlockedStatusArgs = {
  participants: GroupParticipant[];
  locationId: string;
  enabled?: boolean;
};

type GroupBlockedStatus = {
  /** Whether any participant is blocked */
  hasBlockedParticipants: boolean;
  /** Participants who are blocked */
  blockedParticipants: GroupParticipant[];
  /** Participants who are NOT blocked */
  unblockedParticipants: GroupParticipant[];
  /** Whether the queries are still loading */
  isLoading: boolean;
};

/**
 * Checks the blocked status for all participants in a group thread.
 * Uses the batch `useIsBlockedQueries` hook which leverages react-query
 * deduplication — the same query keys will share cache across components.
 */
export const useGroupBlockedStatus = ({
  participants,
  locationId,
  enabled = true,
}: UseGroupBlockedStatusArgs): GroupBlockedStatus => {
  const requests = useMemo(
    () =>
      enabled
        ? participants
            .filter((p) => !!p.phoneNumber)
            .map((p) => ({
              request: {
                locationId,
                personPhone: formatPhoneNumberE164(p.phoneNumber!),
              },
            }))
        : [],
    [participants, locationId, enabled]
  );

  const results = NumberBlockV1.Queries.useIsBlockedQueries(requests);

  return useMemo(() => {
    if (!enabled || participants.length === 0) {
      return {
        hasBlockedParticipants: false,
        blockedParticipants: [],
        unblockedParticipants: participants,
        isLoading: false,
      };
    }

    const participantsWithPhone = participants.filter((p) => !!p.phoneNumber);
    const participantsWithoutPhone = participants.filter((p) => !p.phoneNumber);

    const isLoading = results.some((r) => r.isLoading);

    const blockedParticipants: GroupParticipant[] = [];
    const unblockedParticipants: GroupParticipant[] = [...participantsWithoutPhone];

    participantsWithPhone.forEach((participant, index) => {
      const result = results[index];
      if (result?.data?.isBlocked) {
        blockedParticipants.push(participant);
      } else {
        unblockedParticipants.push(participant);
      }
    });

    return {
      hasBlockedParticipants: blockedParticipants.length > 0,
      blockedParticipants,
      unblockedParticipants,
      isLoading,
    };
  }, [enabled, participants, results]);
};
